import { FocusableOption } from '@angular/cdk/a11y';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  Provider,
  forwardRef,
  inject
} from '@angular/core';

import { Subject, takeUntil } from 'rxjs';

import { Tab } from './tab';
import { TabService } from './tab.service';

@Directive()
export abstract class TabAbstractComponent implements FocusableOption, OnDestroy {
  tab: Tab;
  controls: string;
  @Input() icon: string;
  @Input() iconTitle: string;
  @Input() active = false;
  @Input() disabled = false;
  @Input() ariaControls: string;

  @HostBinding('class.auto-width') @Input() autoWidth = false;
  @HostBinding('attr.id') @Input() id: string;
  @HostBinding('class.is-disabled') isDisabled = false;

  protected unsubscribe = new Subject<void>();

  protected readonly tabService = inject(TabService);
  protected readonly cdr = inject(ChangeDetectorRef);
  readonly element = inject(ElementRef);

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  focus(): void {
    this.element.nativeElement.setAttribute('tabindex', 0);
    this.element.nativeElement.focus();
  }

  reset(): void {
    this.element.nativeElement.setAttribute('tabindex', -1);
  }

  isActive(tab: Tab): boolean {
    return tab.id === this.id;
  }

  changeActive(): void {
    this.tabService.setActive(this.tab || { id: this.id });
  }

  protected setActive(tab: Tab): void {
    /* intentionally left blank */
  }

  protected subscribeToTabChange(): void {
    this.tabService.change.pipe(takeUntil(this.unsubscribe)).subscribe(tab => {
      this.setActive(tab);
    });
  }
}

export function makeTabProviders(type: typeof TabAbstractComponent): Provider[] {
  return [
    {
      provide: TabAbstractComponent,
      useExisting: forwardRef(() => type),
      multi: true
    }
  ];
}
